import { useState, useEffect, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Zap, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSlotBlocks, usePublicAppSettings, useEffectiveSlotConfig, useSlotCapacity, useBlockReschedulePreview } from "@/lib/queries";
import { useBlockRescheduleSuggest, useBlockRescheduleSuggestAi } from "@/lib/mutations";
import { todayIST, dateIST } from "@emr/shared/india";
import { isSlotBlocked, dayUnavailableReason, DEFAULT_WEEKLY_SCHEDULE } from "@emr/shared/scheduling";
import { toast } from "sonner";

/**
 * BlockRescheduleDialog (2026-10-29) — the Block Days flow's OWN reschedule
 * dialog. SEPARATE from the slot-config wizard and from RebookDialog: no
 * shared routes, components, or state. Opened by BlockDaysCard when the
 * admin tries to block a (date, scope) that has stranded appointments —
 * proactively (before POST) or reactively (the 400 appointments_not_all_moved).
 *
 * One dialog lists EVERY appointment stranded on the (date, scope) with a
 * per-row target date + slot + arrival time. Auto-fill (instant, no AI) and
 * Suggest with AI (admin-only route) propose targets; MANUAL PICKS ALWAYS
 * WIN — suggestions fill only rows the admin has not edited.
 *
 * Saving calls the caller's onSubmit(moves) which runs the SAME atomic
 * POST /slot-blocks { ..., appointmentsToMove } the Block Days flow uses —
 * the server re-runs every guard (conflict, must-move, target validation).
 * If the POST still 400s (rows changed mid-flight), the preview refetches
 * so the dialog shows the fresh stranded set.
 */

export interface BlockRescheduleRow {
  id: string;
  patientName: string;
  clinicUhid: string;
  status: string;
  slot: string | null;
  arrivalTime: string | null;
}

export interface BlockRescheduleMove {
  appointmentId: string;
  newDate: string;
  newSlot: string | undefined; // undefined = slot-less date-only pending
  newArrivalTime: string; // ISO — always sent (the move must land on the NEW date)
}

interface RowPick {
  date: string;
  slot: string | null;
  time: string;
}

export function BlockRescheduleDialog({
  open,
  onOpenChange,
  date,
  slot,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** The block target date (YYYY-MM-DD). */
  date: string;
  /** The block scope: null = whole day; a slot key otherwise. */
  slot: string | null;
  /**
   * Called with the per-row moves when the admin saves — the caller runs the
   * atomic POST /slot-blocks (block payload + appointmentsToMove) itself,
   * because the caller owns the block fields (max/reason/scope).
   */
  onSubmit: (moves: BlockRescheduleMove[]) => Promise<void>;
}) {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const today = todayIST();

  // ---- Stranded rows (this flow's OWN preview route). ----
  const preview = useBlockReschedulePreview(date, slot, open);
  const rows: BlockRescheduleRow[] = (preview.data?.rows ?? []) as BlockRescheduleRow[];
  const rowCount = rows.length;

  // ---- Per-row picks + manual-touch tracking (manual picks win). ----
  const [picks, setPicks] = useState<Record<string, RowPick>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Reset whenever the dialog reopens / scope changes.
  useEffect(() => {
    if (open) {
      setPicks({});
      setTouched({});
      setReasons({});
    }
  }, [open, date, slot]);

  // ---- Suggestion mutations (this flow's OWN routes — not the wizard's). ----
  const autoFill = useBlockRescheduleSuggest();
  const suggestAi = useBlockRescheduleSuggestAi();
  const [aiElapsed, setAiElapsed] = useState(0);
  useEffect(() => {
    if (!suggestAi.isPending) return;
    setAiElapsed(0);
    const t = setInterval(() => setAiElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [suggestAi.isPending]);

  const mergeSuggestions = useCallback(
    (suggestions: { id: string; newDate: string | null; newSlot: string | null; newStartTime?: string | null; reason?: string }[]): number => {
      let filled = 0;
      setPicks((prev) => {
        const next = { ...prev };
        for (const s of suggestions) {
          if (touched[s.id]) continue; // manual picks win
          if (!s.newDate) continue;
          // time: explicit pick > the suggested slot's start (server-loaded,
          // same convention as RebookDialog) > blank (slot-less row).
          next[s.id] = { date: s.newDate, slot: s.newSlot ?? null, time: s.newStartTime ?? "" };
          filled++;
        }
        return next;
      });
      setReasons((prev) => {
        const next = { ...prev };
        for (const s of suggestions) {
          if (s.reason) next[s.id] = s.reason;
        }
        return next;
      });
      return filled;
    },
    [touched],
  );

  const doAutoFill = async () => {
    try {
      const res = await autoFill.mutateAsync({ date, slot });
      if (res.suggestions.length === 0) {
        toast.info(res.message ?? "No appointments need rescheduling.");
        return;
      }
      const n = mergeSuggestions(res.suggestions);
      toast.success(`Auto-filled ${n} appointment${n === 1 ? "" : "s"} — review before saving`);
      const missing = res.suggestions.filter((s) => !s.newDate).length;
      if (missing > 0) {
        toast.warning(`${missing} row${missing === 1 ? "" : "s"} had no working slot with capacity — assign manually`);
      }
    } catch { /* onError toasts */ }
  };

  const doSuggestAi = async () => {
    try {
      const res = await suggestAi.mutateAsync({
        date,
        slot,
        currentAssignments: rows
          .filter((r) => picks[r.id]?.date && !touched[r.id])
          .map((r) => ({ appointmentId: r.id, newDate: picks[r.id].date, newSlot: picks[r.id].slot })),
      });
      if (res.error || res.suggestions.length === 0) {
        toast.info(res.message ?? "AI suggestions unavailable");
        return;
      }
      const n = mergeSuggestions(res.suggestions);
      toast.success(`AI proposed ${n} target${n === 1 ? "" : "s"} — review before saving`);
    } catch { /* onError toasts */ }
  };

  // ---- Save: validate every row has a target, then hand the moves over. ----
  const save = async () => {
    const unresolved = rows.filter((r) => !picks[r.id] || !picks[r.id].date);
    if (unresolved.length > 0) {
      toast.error(`${unresolved.length} appointment${unresolved.length === 1 ? "" : "s"} still need a target date`);
      return;
    }
    setSaving(true);
    try {
      const moves: BlockRescheduleMove[] = rows.map((r) => {
        const p = picks[r.id];
        // Arrival derivation — browser-local (IST clinic), the same
        // convention as RebookDialog: explicit time > slot def start > date
        // only. ALWAYS sent: POST /slot-blocks keeps the OLD arrival when
        // newArrivalTime is absent, which would strand the row on the
        // blocked date. Slot-less rows use explicit +05:30 midnight (the
        // review-flow date-only convention) — never a UTC-shifted date.
        const arrivalIso = p.slot
          ? // Slotted: browser-local IST wall time (same construction as
            // BookDialog/RebookDialog) — 09:00 IST = 03:30 UTC same day, so
            // date() lands on p.date in any session tz.
            new Date(`${p.date}T${p.time || "09:00"}:00`).toISOString()
          : // Slot-less (review-flow date-only): UTC MIDNIGHT — the server's
            // own convention (slotBlocks.ts move path: `${newDate}T00:00:00Z`).
            // IST midnight (+05:30 = 18:30 UTC previous day) would date()
            // to the PREVIOUS day and re-strand the row.
            `${p.date}T00:00:00.000Z`;
        return {
          appointmentId: r.id,
          newDate: p.date,
          // undefined (not null) = slot-less: the server's moveSchema.newSlot
          // is optional and the slot-less branch (Pending + slot null) keys
          // off newArrivalTime, which we ALWAYS send — the row stays slot-less.
          newSlot: p.slot ?? undefined,
          newArrivalTime: arrivalIso,
        };
      });
      await onSubmit(moves);
      onOpenChange(false);
    } catch (e) {
      const err = e as Error & { body?: { error?: string; message?: string } };
      if (err?.body?.error === "appointments_not_all_moved") {
        // Rows changed mid-flight — refetch so the dialog shows the fresh
        // stranded set instead of stale picks. No toast here: the mutation's
        // own onError already surfaced the server message.
        void queryClient.invalidateQueries({ queryKey: ["block-reschedule-preview"] });
      }
      // Other errors: the mutation's onError already toasted the message.
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || autoFill.isPending || suggestAi.isPending || preview.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Reschedule before blocking {date}
            {slot ? ` — ${slot}` : " (whole day)"}
          </DialogTitle>
        </DialogHeader>

        {preview.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Loader2 className="animate-spin" size={14} /> Loading affected appointments…
          </div>
        ) : preview.isError ? (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
            Could not load the affected appointments. Close and try again.
          </div>
        ) : rowCount === 0 ? (
          <div className="rounded-lg border border-dashed border-cream-200 p-6 text-center text-sm text-muted dark:border-dark-700">
            No appointments are stranded on this scope — the block can be applied directly.
          </div>
        ) : (
          <>
            <p className="text-xs text-muted">
              {rowCount} appointment{rowCount === 1 ? "" : "s"} must move before this block can be saved.
              Auto-fill proposes the nearest working slot with capacity; AI can refine further (admin-only).
              Manual picks always win. Saving applies the block and moves every appointment in one atomic request.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={doAutoFill} disabled={autoFill.isPending || suggestAi.isPending}>
                {autoFill.isPending ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                Auto-fill
              </Button>
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={doSuggestAi} disabled={suggestAi.isPending || autoFill.isPending}>
                  {suggestAi.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {suggestAi.isPending ? `AI… ${aiElapsed}s` : "Suggest with AI"}
                </Button>
              )}
            </div>
            <div className="max-h-80 space-y-2 overflow-auto pr-1">
              {rows.map((r) => (
                <RescheduleRow
                  key={r.id}
                  row={r}
                  pick={picks[r.id]}
                  reason={reasons[r.id]}
                  suggested={!touched[r.id] && !!picks[r.id]}
                  onPick={(p) => {
                    setPicks((prev) => ({ ...prev, [r.id]: p }));
                    setTouched((prev) => ({ ...prev, [r.id]: true }));
                  }}
                  minDate={today}
                />
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || rowCount === 0} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            {saving ? "Saving…" : `Save block & move ${rowCount || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SLOTLESS_KEY = "__none__"; // Radix Select forbids empty-string values

function RescheduleRow({
  row,
  pick,
  reason,
  suggested,
  onPick,
  minDate,
}: {
  row: BlockRescheduleRow;
  pick?: RowPick;
  reason?: string;
  suggested: boolean;
  onPick: (p: RowPick) => void;
  minDate: string;
}) {
  const date = pick?.date ?? "";
  // NEVER `pick?.slot ?? row.slot` — an explicit slot-LESS pick (null) would
  // fall through to the row's current slot key and be silently lost.
  const slot = pick ? pick.slot : (row.slot ?? null);
  const time = pick?.time ?? "";

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return dateIST(d);
  }, []);

  // The TARGET date's config — the slot picker offers its keys (same source
  // BookDaysCard/RebookDialog trust). Graceful-degrades to the legacy list.
  const { data: settings } = usePublicAppSettings();
  const { data: blocksData } = useSlotBlocks();
  const blocks = (blocksData?.rows ?? []) as Parameters<typeof isSlotBlocked>[2];
  const { slotList } = useEffectiveSlotConfig(date || undefined, settings);
  const { data: capacity } = useSlotCapacity(date || undefined);

  const targetDateObj = new Date((date || minDate) + "T00:00:00");
  const weeklySchedule = (settings?.weeklySchedule as never) ?? DEFAULT_WEEKLY_SCHEDULE;
  const unavailable = date ? dayUnavailableReason(targetDateObj, weeklySchedule, blocks) : null;

  const capFor = (key: string): { booked: number; max: number } | undefined => {
    const fromMap = (capacity as { slots?: Record<string, { booked: number; pending: number; max: number }> } | undefined)?.slots?.[key];
    if (fromMap) return { booked: fromMap.booked, max: fromMap.max };
    if (key === "Morning") return capacity?.Morning;
    if (key === "Evening") return capacity?.Evening;
    return undefined;
  };

  const setSlotKey = (key: string) => {
    if (key === SLOTLESS_KEY) {
      onPick({ date, slot: null, time: "" });
      return;
    }
    const def = slotList.find((s) => s.key === key);
    onPick({ date, slot: key, time: def?.startTime ?? time });
  };

  return (
    <div className="rounded-lg border border-cream-200 p-3 dark:border-dark-700">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">
            {row.patientName}
            {suggested && <Badge variant="outline" className="ml-2 text-[10px] text-muted">suggested</Badge>}
          </div>
          <div className="text-xs text-muted">
            {row.clinicUhid} · {row.status}{row.slot ? ` · ${row.slot}` : " · no slot"}
            {row.arrivalTime ? ` · ${new Date(row.arrivalTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
          </div>
        </div>
        {reason && <div className="max-w-[16rem] truncate text-[11px] text-muted" title={reason}>{reason}</div>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">New date</Label>
          <Input
            type="date"
            value={date}
            min={minDate}
            max={maxDate}
            onChange={(e) => onPick({ date: e.target.value, slot, time })}
          />
          {date && unavailable && (
            <div className="mt-1 text-[11px] font-medium text-red-600">
              {unavailable.kind === "blocked" ? "Whole day blocked" : "Not a working day"}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Slot</Label>
          <Select value={slot ?? SLOTLESS_KEY} onValueChange={setSlotKey}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Pick slot…" /></SelectTrigger>
            <SelectContent>
              {/* Only slot-LESS rows (review-flow pendings) may stay slot-less —
                  a slot-bound row must keep a slot key (server move path). */}
              {row.slot === null && <SelectItem value={SLOTLESS_KEY}>(no slot — date only)</SelectItem>}
              {slotList.map((def) => {
                const cap = capFor(def.key);
                const blocked = date ? isSlotBlocked(new Date(date + "T00:00:00"), def.key, blocks) : false;
                const full = cap ? cap.max > 0 && cap.booked >= cap.max : false;
                return (
                  <SelectItem key={def.key} value={def.key} disabled={blocked}>
                    {def.label}{cap ? ` (${cap.booked}${cap.max > 0 ? `/${cap.max}` : ""}${full ? " · full" : ""})` : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Arrival {slot ? "" : "(ignored)"}</Label>
          <Input
            type="time"
            value={time}
            disabled={!slot}
            onChange={(e) => onPick({ date, slot, time: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}